import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { array, bool, object, shape, string } from 'prop-types';
import { PrismicRichText } from '@prismicio/react';

import {
  ArticleCard,
  Footer,
  LayoutSingleColumn,
  LayoutWrapperFooter,
  LayoutWrapperMain,
  LayoutWrapperTopbar,
  Page,
} from '../../components';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import PrismicSectionHero from '../../components/PrismicSectionHero/PrismicSectionHero';
import { formatArticlePrismicData } from '../../util/data';
import { propTypes } from '../../util/types';
import { formatPrismicDate } from '../../util/dates';

import css from './KnowledgeHubPage.module.css';

const formatDateOptions = { year: 'numeric', month: 'long', day: 'numeric' };

const KnowledgeHubPageComponent = props => {
  const {
    params: rawParams,
    intl,
    article,
    relatedArticles = [],
    scrollingDisabled,
    fetchArticleInProgress,
    fetchArticleError,
  } = props;

  const { slug } = rawParams;
  const {
    title,
    postDate,
    postedBy,
    image,
    alt,
    content,
    metaTitle,
    metaDescription,
  } = formatArticlePrismicData(article);

  const formattedPostDate = formatPrismicDate(postDate);

  const schemaTitle =
    metaTitle || title || intl.formatMessage({ id: 'KnowledgeHubPage.schemaTitle' });

  return (
    <Page
      title={schemaTitle}
      description={metaDescription}
      scrollingDisabled={scrollingDisabled}
      contentType="website"
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        description: metaDescription,
        name: schemaTitle,
        image: image,
      }}
    >
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer hideSearchbar />
        </LayoutWrapperTopbar>
        <LayoutWrapperMain>
          <PrismicSectionHero
            backgroundImage={image}
            intl={intl}
            linkParams={{ slug }}
            className={css.prismicSectionHero}
            alt={alt}
            disableLink
          />
          <div className={css.wrapper}>
            {fetchArticleError && (
              <p className={css.error}>
                <FormattedMessage id="KnowledgeHubPage.error" />
              </p>
            )}
            {fetchArticleInProgress && (
              <p>
                <FormattedMessage id="KnowledgeHubPage.loading" />
              </p>
            )}
            <h1 className={css.title}>{title}</h1>
            <p className={css.metaInfo}>
              {formattedPostDate}
            </p>
            <p className={css.metaInfo}>
              {intl.formatMessage({ id: 'KnowledgeHubPage.postedBy' }, { writer: postedBy })}
            </p>
            <div className={css.contentWrapper}>
              <PrismicRichText field={content} />
            </div>
            {relatedArticles.length > 0 && (
              <>
                <div className={css.heading}>
                  {intl.formatMessage({ id: 'KnowledgeHubPage.relatedPosts' })}
                </div>
                <div className={css.relatedArticles}>
                  {relatedArticles.map(article => (
                    <ArticleCard key={article.id} article={article} className={css.article} />
                  ))}
                </div>
              </>
            )}
          </div>
        </LayoutWrapperMain>
        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </Page>
  );
};

KnowledgeHubPageComponent.defaultProps = {
  params: null,
  article: null,
  fetchArticleInProgress: false,
  relatedArticles: [],
  fetchArticleError: null,
};

KnowledgeHubPageComponent.propTypes = {
  params: shape({
    slug: string,
  }),
  article: object,
  relatedArticles: array,
  fetchArticleInProgress: bool,
  scrollingDisabled: bool.isRequired,
  fetchArticleError: propTypes.error,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const {
    fetchArticleInProgress,
    fetchArticleError,
    article,
    relatedArticles,
  } = state.KnowledgeHubPage;

  return {
    fetchArticleInProgress,
    fetchArticleError,
    article,
    scrollingDisabled: isScrollingDisabled(state),
    relatedArticles,
  };
};

const KnowledgeHubPage = compose(
  withRouter,
  connect(mapStateToProps),
  injectIntl
)(KnowledgeHubPageComponent);

export default KnowledgeHubPage;
